// Profile menu — popover with account info + sign-out.
//
// Reads useAuth() (stub from CHG-1167; account: null until ε CHG-1170 lands
// real auth). Surfaces a placeholder email when unauthenticated so the shell
// has visible chrome.
//
// Per .forge/plans/v2-nav-shell.md.

import * as LucideIcons from "lucide-react";
import { type ComponentType, type RefObject } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { usePopover } from "./usePopover";
// CHG-1278 — additive: "Use classic dashboard" fallback link (permanent
// dual-host coexistence per project_alora_dashboard_v1_v2_coexistence.md).
// Hides itself when no v1 host mapping is available (localhost / IP /
// unrecognized brand variant) so dev environments stay clean.
import { ClassicDashboardLink } from "./ClassicDashboardLink";

type IconCmp = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean | "true";
}>;

function initials(name: string | null | undefined): string {
  if (!name) return "··";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "··";
}

export function ProfileMenu() {
  const { account, logout } = useAuth();
  const popover = usePopover();
  const email = account?.email ?? "unauthenticated@example.com";
  const fullName = account?.full_name ?? null;
  const { LogOut, Settings: SettingsIcon } = LucideIcons as unknown as {
    LogOut: IconCmp;
    Settings: IconCmp;
  };

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        ref={popover.anchorRef as RefObject<HTMLButtonElement>}
        type="button"
        aria-haspopup="menu"
        aria-expanded={popover.open}
        aria-label="Profile menu"
        onClick={popover.toggle}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          padding: 0,
          background: "var(--color-primary-tint)",
          color: "var(--color-primary)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: 9999,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        {initials(fullName)}
      </button>
      {popover.open && (
        <div
          ref={popover.contentRef as RefObject<HTMLDivElement>}
          role="menu"
          aria-label="Account menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 900,
            width: 240,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "var(--radius-md, 8px)",
            boxShadow: "var(--shadow-medium)",
            padding: 6,
          }}
        >
          <div
            style={{
              padding: "10px 12px",
              borderBottom: "1px solid var(--color-border-subtle)",
              marginBottom: 4,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}>
              {fullName ?? "Unsigned-in user"}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--color-text-muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {email}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={popover.close}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "8px 10px",
              textAlign: "left",
              border: "none",
              background: "transparent",
              color: "var(--color-text)",
              fontSize: 13,
              borderRadius: "var(--radius-sm, 6px)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <SettingsIcon size={14} aria-hidden="true" />
            <span style={{ flex: 1 }}>Account settings</span>
            <span
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--color-text-muted)",
                border: "1px solid var(--color-border-subtle)",
                borderRadius: 4,
                padding: "1px 6px",
              }}
            >
              Soon
            </span>
          </button>
          {/* CHG-1278 — v1 fallback link, additive between Account settings
              and Sign out. Hides itself in dev environments (no v1 host mapping). */}
          <ClassicDashboardLink onNavigate={popover.close} />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              logout();
              popover.close();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "8px 10px",
              textAlign: "left",
              border: "none",
              background: "transparent",
              color: "var(--color-text)",
              fontSize: 13,
              borderRadius: "var(--radius-sm, 6px)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <LogOut size={14} aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
